import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { BookOpen, Sparkles, Trash2, Eye, EyeOff, Send, X } from "lucide-react";

export default function AdminKnowledge() {
  const [suggestions, setSuggestions] = useState(null);
  const [articles, setArticles] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => {
    api.get("/admin/knowledge-suggestions").then((r) => setSuggestions(r.data)).catch(() => setSuggestions([]));
    api.get("/admin/knowledge").then((r) => setArticles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const publish = async () => {
    await api.post(`/admin/knowledge-suggestions/${edit.suggestion_id}/publish`, { title: edit.title, answer: edit.answer, category: edit.category });
    toast.success("Published to Knowledge Centre"); setEdit(null); load();
  };
  const dismiss = async (id) => { await api.put(`/admin/knowledge-suggestions/${id}/dismiss`, {}); load(); };
  const togglePublish = async (a) => { await api.put(`/admin/knowledge/${a.slug}`, { published: !(a.published !== false) }); load(); };
  const del = async (slug) => { if (!window.confirm("Delete this article?")) return; await api.delete(`/admin/knowledge/${slug}`); toast.success("Deleted"); load(); };

  if (!suggestions) return <Loading />;
  return (
    <div>
      <PageHeader title="Knowledge Centre & SEO" subtitle="Publish the most-asked questions as public articles Google can index." />
      <Tabs defaultValue="suggestions">
        <TabsList className="mb-6">
          <TabsTrigger value="suggestions" data-testid="kn-tab-suggestions">Suggestions ({suggestions.length})</TabsTrigger>
          <TabsTrigger value="published" data-testid="kn-tab-published">Published ({articles.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="suggestions">
          {suggestions.length === 0 ? <EmptyState icon={Sparkles} title="No suggestions yet" subtitle="Questions asked via Ask SocietyMitra are collected here, ranked by how often they're asked." /> : (
            <div className="space-y-3">
              {suggestions.map((s) => (
                <div key={s.suggestion_id} className="bg-card border rounded-xl p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`kn-sugg-${s.suggestion_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold bg-secondary/10 text-secondary rounded-full px-2 py-0.5">Asked {s.hits}×</span>
                      <span className="text-xs text-muted-foreground">{s.source === "public_ask" ? "Landing" : "Portal"}</span>
                    </div>
                    <div className="font-medium mt-1">{s.question}</div>
                    <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.answer}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" className="rounded-full gap-1.5" onClick={() => setEdit({ ...s, title: s.question, category: "Housing Society FAQ" })} data-testid={`kn-publish-${s.suggestion_id}`}><Send className="w-3.5 h-3.5" /> Review & Publish</Button>
                    <Button size="sm" variant="ghost" onClick={() => dismiss(s.suggestion_id)} data-testid={`kn-dismiss-${s.suggestion_id}`}><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="published">
          {articles.length === 0 ? <EmptyState icon={BookOpen} title="No articles yet" subtitle="Publish suggestions to build your SEO knowledge base." /> : (
            <div className="space-y-3">
              {articles.map((a) => (
                <div key={a.slug} className="bg-card border rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`kn-article-${a.slug}`}>
                  <div className="min-w-0">
                    <div className="font-medium">{a.title}</div>
                    <div className="text-xs text-muted-foreground font-mono">/knowledge/{a.slug}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-1.5 text-xs">{a.published !== false ? <Eye className="w-3.5 h-3.5 text-secondary" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}<Switch checked={a.published !== false} onCheckedChange={() => togglePublish(a)} data-testid={`kn-toggle-${a.slug}`} /></div>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(a.slug)} data-testid={`kn-del-${a.slug}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="kn-publish-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Review & Publish Article</DialogTitle><DialogDescription>Edit before publishing. This page will be public and indexable by Google.</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label className="text-sm mb-1 block">Title (SEO headline)</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="kn-edit-title" /></div>
              <div><Label className="text-sm mb-1 block">Category</Label><Input value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} /></div>
              <div><Label className="text-sm mb-1 block">Answer / Body</Label><Textarea rows={10} value={edit.answer} onChange={(e) => setEdit({ ...edit, answer: e.target.value })} data-testid="kn-edit-answer" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={publish} className="rounded-full gap-2" data-testid="kn-publish-confirm"><Send className="w-4 h-4" /> Publish</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
